import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useLayoutContext } from '../../context/layoutContext'
import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import {
  type ConfigurationTemplateFunctionValue,
  type DynamicConfigurationTemplateFunctionValue,
  isNotRelevantField,
  isSubTemplatedField,
  isSubTemplatedFieldValue,
  isTemplateField,
  isTemplateVersion,
  type TemplateVersion,
  ValueSetupRole,
} from '../../types/templateModel'
import { enrichConfigurationField } from '../../uiModels/helpers'
import {
  functionPath,
  generateDynamicConfigurationTemplateFunctionValue,
} from '../../uiModels/transformers/toVisualModelConverter'
import { addDynamicFunctionToSubTemplatedField } from '../dynamicFunction/helpers'
import type { AddDynamicFunctionPlaceholder } from '../dynamicFunction/types'
import type { ConfigurationTemplateFunctionValueWithPosition } from './fieldsHelpers'
import type { TemplateFieldTableParent } from './TemplateFieldsTable'

type Props = {
  parent: TemplateFieldTableParent
  declaredFunctions?: Optional<ConfigurationTemplateFunctionValue[]>
  dynamicFunctions?: Optional<ConfigurationTemplateFunctionValue[]>
}

export const useTemplateFieldTableFunctions = ({
  parent,
  declaredFunctions,
  dynamicFunctions,
}: Props) => {
  const { isDraftTemplate, templateVersion } = useTemplateContext()
  const {
    preferences: {
      templates: { isShowingEmptyValues },
    },
  } = useLayoutContext()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)

  const canAddDynamicFunction =
    (isSubTemplatedFieldValue(parent) &&
      !isNotRelevantField(parent.subTemplatedField)) ||
    isTemplateVersion(parent) ||
    (isTemplateField(parent) &&
      isSubTemplatedField(parent) &&
      parent.role === ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE &&
      !isNotRelevantField(parent))

  return useMemo((): (
    | ConfigurationTemplateFunctionValueWithPosition
    | AddDynamicFunctionPlaceholder
  )[] => {
    const dynamicFunctionPlaceholder = (): AddDynamicFunctionPlaceholder[] => {
      if (!canAddDynamicFunction || !isDraftTemplate || !isShowingEmptyValues) {
        return []
      }

      if (isTemplateVersion(parent)) {
        return [
          {
            type: 'addDynamicFunctionPlaceholder',
            parent,
            addRawFunction: (value) => {
              const enrichedValue = enrichConfigurationField({
                value,
                metaModel,
                path: functionPath({
                  basePath: parent.path,
                  source: value.function,
                }),
              })
              assertNotEmpty(enrichedValue)

              const dynamicFunction =
                generateDynamicConfigurationTemplateFunctionValue({
                  value: enrichedValue,
                  path: parent.path,
                })

              void parent.updateTemplate({
                ...parent,
                dynamicFunctions: [
                  ...(parent.dynamicFunctions ?? []),
                  dynamicFunction as DynamicConfigurationTemplateFunctionValue,
                ],
              } satisfies TemplateVersion)
            },
          } satisfies AddDynamicFunctionPlaceholder,
        ]
      } else {
        const subTemplateIdentifier = isSubTemplatedFieldValue(parent)
          ? parent.subTemplateIdentifier
          : parent.value!.availableVariantTypes[0].typeName

        return [
          {
            type: 'addDynamicFunctionPlaceholder',
            parent: isSubTemplatedFieldValue(parent)
              ? parent.subTemplatedField!
              : parent,
            addRawFunction: (value) => {
              const enrichedValue = enrichConfigurationField({
                value,
                metaModel,
                path: functionPath({
                  basePath: parent.path,
                  source: value.function,
                }),
              })
              assertNotEmpty(enrichedValue)

              const dynamicFunction =
                generateDynamicConfigurationTemplateFunctionValue({
                  value: enrichedValue,
                  path: parent.path,
                })

              const subTemplatedField = isTemplateField(parent)
                ? parent
                : parent.subTemplatedField

              assertNotEmpty(dynamicFunction)
              assertNotEmpty(subTemplatedField)

              addDynamicFunctionToSubTemplatedField({
                dynamicFunction:
                  dynamicFunction as DynamicConfigurationTemplateFunctionValue,
                metaModel,
                subTemplateIdentifier,
                subTemplatedField,
              })
            },
            subTemplateIdentifier,
          } satisfies AddDynamicFunctionPlaceholder,
        ]
      }
    }

    return [
      ...(declaredFunctions ?? [])
        .filter((fn) =>
          isShowingEmptyValues ? fn : !!fn.function?.fplusSource,
        )
        .map((fn, position) => ({
          ...fn,
          position,
        })),
      ...(dynamicFunctions ?? []).map((fn, position) => ({
        ...fn,
        position,
      })),
      ...dynamicFunctionPlaceholder(),
    ]
  }, [
    canAddDynamicFunction,
    declaredFunctions,
    dynamicFunctions,
    isDraftTemplate,
    isShowingEmptyValues,
    metaModel,
    parent,
  ])
}
